import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabase";
import "./flowline-theme.css";
import { withUploadType } from "../uploadType";
import { blockUser, reportContent } from "../moderation";

export const API_BASE_URL = process.env.REACT_APP_API_URL || "https://backend-long-morning-8596.fly.dev";
const AVATAR_BUCKET = "avatars";

const UNSUPPORTED_IMAGE_EXTENSIONS = new Set(["heic", "heif"]);

const SKILL_LEVELS = ["beginner", "intermediate", "advanced", "expert"];
const RUN_OPTIONS = ["groomers", "moguls", "park", "backcountry", "powder", "trees"];
const DAY_OPTIONS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const TIME_OPTIONS = ["morning", "afternoon", "evening"];

function toggleInList(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function fileExtension(filename) {
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "jpg";
}

function formatMatchedAt(isoString) {
  const then = new Date(isoString);
  const diffMs = Date.now() - then.getTime();
  const diffMins = Math.round(diffMs / 60000);
  if (diffMins < 60) return diffMins <= 1 ? "just now" : `${diffMins} min ago`;
  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return then.toLocaleDateString();
}

function initials(name) {
  if (!name) return "?";
  const chars = name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || "");
  return chars.join("") || "?";
}

function TagRow({ items, rowStyle, chipStyle }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={rowStyle || styles.tagRow}>
      {items.map((item) => (
        <span key={item} style={chipStyle || styles.tagChip}>
          {item}
        </span>
      ))}
    </div>
  );
}

function MountainIcon() {
  return (
    <svg width="56%" viewBox="0 0 240 140" style={{ display: "block" }}>
      <polygon points="0,140 70,32 112,92 152,22 240,140" fill="rgba(255,255,255,0.16)" />
      <polygon points="40,140 122,62 172,112 240,140" fill="rgba(255,255,255,0.26)" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M6 6L18 18M18 6L6 18" stroke="#c7d4e0" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="#0d1b2a">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

function IconCamera() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 8a2 2 0 0 1 2-2h1.2l.8-1.6a1 1 0 0 1 .9-.4h6.2a1 1 0 0 1 .9.6L16.8 6H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"
        stroke="#ffffff"
        strokeWidth="1.6"
      />
      <circle cx="12" cy="13" r="3.2" stroke="#ffffff" strokeWidth="1.6" />
    </svg>
  );
}

function DiscoverCard({ candidate, isRemoving, isSwiping, onOpenProfile, onSwipe }) {
  const availability = candidate.availability || {};
  const photoCount = (candidate.photos || []).length;

  return (
    <div
      onClick={() => onOpenProfile(candidate)}
      style={{
        ...styles.discoverCard,
        opacity: isRemoving ? 0 : 1,
        transform: isRemoving ? "scale(0.85)" : "scale(1)",
      }}
    >
      <div style={styles.cardPhotoWrap}>
        {candidate.avatar_url ? (
          <img src={candidate.avatar_url} alt="" style={styles.cardPhotoImg} />
        ) : (
          <div style={styles.cardPhotoPlaceholder}>
            <span style={styles.cardPhotoInitial}>{initials(candidate.display_name)}</span>
          </div>
        )}

        <div style={styles.cardScrim} />

        {photoCount > 1 && (
          <div style={styles.cardPhotoCountBadge}>
            <IconCamera />
            <span>{photoCount}</span>
          </div>
        )}

        <div style={styles.cardNameOverlay}>
          <h3 style={styles.cardName}>{candidate.display_name}</h3>
          <span style={styles.cardSkillBadge}>{candidate.skill_level}</span>
        </div>

        <div style={styles.cardActionsRow}>
          <button
            type="button"
            style={styles.cardPassButton}
            onClick={(e) => {
              e.stopPropagation();
              onSwipe(candidate, "pass");
            }}
            disabled={isSwiping || isRemoving}
            aria-label="Pass"
          >
            <IconClose />
          </button>
          <button
            type="button"
            style={styles.cardLikeButton}
            onClick={(e) => {
              e.stopPropagation();
              onSwipe(candidate, "like");
            }}
            disabled={isSwiping || isRemoving}
            aria-label="Like"
          >
            <IconHeart />
          </button>
        </div>
      </div>

      <div style={styles.cardBody}>
        <TagRow items={candidate.preferred_runs} rowStyle={styles.cardTagRow} chipStyle={styles.cardTagChip} />
        <TagRow items={availability.days} rowStyle={styles.cardTagRow} chipStyle={styles.cardTagChip} />
        <TagRow items={availability.time_of_day} rowStyle={styles.cardTagRow} chipStyle={styles.cardTagChip} />
        {candidate.bio && <p style={styles.cardBio}>{candidate.bio}</p>}
      </div>
    </div>
  );
}

export async function apiFetch(path, options = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) {
    throw new Error("You need to be signed in to use Buddy Finder.");
  }
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body.detail || detail;
    } catch (e) {}
    const err = new Error(detail);
    err.status = response.status;
    throw err;
  }
  if (response.status === 204) return null;
  return response.json();
}

export default function BuddyFinderMode({ onMessagesRead } = {}) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("discover");
  const [previousTab, setPreviousTab] = useState("discover");

  const [displayName, setDisplayName] = useState("");
  const [skillLevel, setSkillLevel] = useState("intermediate");
  const [preferredRuns, setPreferredRuns] = useState([]);
  const [days, setDays] = useState([]);
  const [timesOfDay, setTimesOfDay] = useState([]);
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [deletingPhotoIds, setDeletingPhotoIds] = useState(() => new Set());

  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [swipingIds, setSwipingIds] = useState(() => new Set());
  const [removingIds, setRemovingIds] = useState(() => new Set());
  const [justMatched, setJustMatched] = useState(null);

  const [viewedProfile, setViewedProfile] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [activeMatch, setActiveMatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageDraft, setMessageDraft] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch("/buddy-finder/profile/me");
      setProfile(data);
    } catch (err) {
      if (err.status === 404) {
        setProfile(null);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data?.session?.user?.id || null);
    });
  }, []);

  async function saveProfile(overrides = {}) {
    if (!displayName.trim()) {
      setError("Please enter a display name.");
      return null;
    }
    const saved = await apiFetch("/buddy-finder/profile", {
      method: "PUT",
      body: JSON.stringify({
        display_name: displayName.trim(),
        skill_level: skillLevel,
        preferred_runs: preferredRuns,
        availability: { days, time_of_day: timesOfDay },
        bio: bio.trim() || null,
        avatar_url: avatarUrl || null,
        is_active: true,
        ...overrides,
      }),
    });
    setProfile(saved);
    return saved;
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await saveProfile();
      if (tab === "profile") {
        setTab(previousTab);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function openProfileEditor() {
    setDisplayName(profile.display_name || "");
    setSkillLevel(profile.skill_level || "intermediate");
    setPreferredRuns(profile.preferred_runs || []);
    setDays(profile.availability?.days || []);
    setTimesOfDay(profile.availability?.time_of_day || []);
    setBio(profile.bio || "");
    setAvatarUrl(profile.avatar_url || "");
    setError(null);
    setPreviousTab(tab === "profile" ? previousTab : tab);
    setTab("profile");
  }

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!displayName.trim()) {
      setError("Please enter a display name before uploading a photo.");
      e.target.value = "";
      return;
    }
    const ext = fileExtension(file.name).toLowerCase();
    if (UNSUPPORTED_IMAGE_EXTENSIONS.has(ext)) {
      setError("HEIC photos aren't supported yet — please use JPG, PNG, or another common image format.");
      e.target.value = "";
      return;
    }
    setAvatarUploading(true);
    setError(null);
    try {
      const { upload_url, avatar_path, token } = await apiFetch("/buddy-finder/avatar-upload-url", {
        method: "POST",
        body: JSON.stringify({ file_extension: ext }),
      });
      void upload_url;

      const { error: uploadError } = await supabase.storage
        .from(AVATAR_BUCKET)
        .uploadToSignedUrl(avatar_path, token, withUploadType(file));
      if (uploadError) {
        throw new Error(uploadError.message || "Photo upload failed.");
      }

      const { data: publicUrlData } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(avatar_path);
      const newAvatarUrl = publicUrlData.publicUrl;
      setAvatarUrl(newAvatarUrl);
      await saveProfile({ avatar_url: newAvatarUrl });
    } catch (err) {
      setError(err.message);
    } finally {
      setAvatarUploading(false);
    }
  }

  const loadGalleryPhotos = useCallback(async () => {
    setGalleryLoading(true);
    try {
      const data = await apiFetch("/buddy-finder/photos/me");
      setGalleryPhotos(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "profile" || !profile) {
      loadGalleryPhotos();
    }
  }, [tab, profile, loadGalleryPhotos]);

  async function handleGalleryPhotoAdd(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = fileExtension(file.name).toLowerCase();
    if (UNSUPPORTED_IMAGE_EXTENSIONS.has(ext)) {
      setError("HEIC photos aren't supported yet — please use JPG, PNG, or another common image format.");
      e.target.value = "";
      return;
    }
    setGalleryUploading(true);
    setError(null);
    try {
      const { upload_url, photo_path, token } = await apiFetch("/buddy-finder/photos/upload-url", {
        method: "POST",
        body: JSON.stringify({ file_extension: ext }),
      });
      void upload_url;

      const { error: uploadError } = await supabase.storage
        .from(AVATAR_BUCKET)
        .uploadToSignedUrl(photo_path, token, withUploadType(file));
      if (uploadError) {
        throw new Error(uploadError.message || "Photo upload failed.");
      }

      const { data: publicUrlData } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(photo_path);
      const saved = await apiFetch("/buddy-finder/photos", {
        method: "POST",
        body: JSON.stringify({ photo_url: publicUrlData.publicUrl }),
      });
      setGalleryPhotos((prev) => [...prev, saved]);
    } catch (err) {
      setError(err.message);
    } finally {
      setGalleryUploading(false);
    }
  }

  async function handleDeletePhoto(photoId) {
    setDeletingPhotoIds((prev) => new Set(prev).add(photoId));
    setError(null);
    try {
      await apiFetch(`/buddy-finder/photos/${photoId}`, { method: "DELETE" });
      setGalleryPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingPhotoIds((prev) => {
        const next = new Set(prev);
        next.delete(photoId);
        return next;
      });
    }
  }

  const loadCandidates = useCallback(async () => {
    setCandidatesLoading(true);
    setError(null);
    try {
      const data = await apiFetch("/buddy-finder/candidates?limit=20");
      setCandidates(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (profile && tab === "discover") {
      loadCandidates();
    }
  }, [profile, tab, loadCandidates]);

  const CARD_REMOVE_MS = 250;

  async function handleSwipe(candidate, direction) {
    if (swipingIds.has(candidate.id)) return;
    setSwipingIds((prev) => new Set(prev).add(candidate.id));
    setRemovingIds((prev) => new Set(prev).add(candidate.id));
    setTimeout(() => {
      setCandidates((prev) => prev.filter((c) => c.id !== candidate.id));
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(candidate.id);
        return next;
      });
    }, CARD_REMOVE_MS);
    try {
      const result = await apiFetch("/buddy-finder/swipe", {
        method: "POST",
        body: JSON.stringify({ swipee_id: candidate.id, direction }),
      });
      if (result.matched) {
        setJustMatched(candidate);
      }
    } catch (err) {
      console.error("Swipe failed:", err);
    } finally {
      setSwipingIds((prev) => {
        const next = new Set(prev);
        next.delete(candidate.id);
        return next;
      });
    }
  }

  const loadMatches = useCallback(async () => {
    setMatchesLoading(true);
    setError(null);
    try {
      const data = await apiFetch("/buddy-finder/matches");
      setMatches(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setMatchesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (profile && tab === "matches") {
      loadMatches();
    }
  }, [profile, tab, loadMatches]);

  const loadMessages = useCallback(async (matchId) => {
    try {
      const data = await apiFetch(`/buddy-finder/matches/${matchId}/messages`);
      setMessages(data);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!activeMatch) return;
    loadMessages(activeMatch.match_id).then((ok) => {
      if (!ok) return;
      // Opening the chat marks the other person's messages as read on the
      // backend, so reflect that locally right away instead of waiting for
      // the next matches/unread-count poll.
      setMatches((prev) =>
        prev.map((m) => (m.match_id === activeMatch.match_id ? { ...m, has_unread: false } : m))
      );
      onMessagesRead?.();
    });
    const interval = setInterval(() => loadMessages(activeMatch.match_id), 4000);
    return () => clearInterval(interval);
  }, [activeMatch, loadMessages, onMessagesRead]);

  async function handleSendMessage(e) {
    e.preventDefault();
    const body = messageDraft.trim();
    if (!body || sendingMessage || !activeMatch) return;
    setSendingMessage(true);
    setMessageDraft("");
    try {
      const sent = await apiFetch(`/buddy-finder/matches/${activeMatch.match_id}/messages`, {
        method: "POST",
        body: JSON.stringify({ body }),
      });
      setMessages((prev) => [...prev, sent]);
    } catch (err) {
      setError(err.message);
      setMessageDraft(body);
    } finally {
      setSendingMessage(false);
    }
  }

  async function handleBlock(buddy) {
    if (!(await blockUser(apiFetch, buddy.id, buddy.display_name))) return;
    // The backend leaves them out of candidates, matches and chat from here on; match that now.
    setCandidates((prev) => prev.filter((c) => c.id !== buddy.id));
    setMatches((prev) => prev.filter((m) => m.buddy.id !== buddy.id));
    setActiveMatch((prev) => (prev && prev.buddy.id === buddy.id ? null : prev));
    setViewedProfile(null);
  }

  const viewedProfilePhotos = viewedProfile
    ? [viewedProfile.avatar_url, ...(viewedProfile.photos || [])].filter(Boolean)
    : [];

  if (loading) {
    return (
      <div className="fl-theme" style={styles.page}>
        <p style={styles.muted}>Loading...</p>
      </div>
    );
  }

  const profileForm = (
    <form onSubmit={handleSaveProfile}>
            <div style={styles.field}>
              <label style={styles.label}>Display name</label>
              <input
                style={styles.input}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={80}
                placeholder="e.g. Jan M."
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Profile photo</label>
              <div style={styles.avatarEditOuter}>
                <div style={styles.avatarEditPhotoBox}>
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" style={styles.avatarEditImg} />
                  ) : (
                    <div style={styles.cardPhotoPlaceholder}>
                      <span style={styles.avatarEditInitial}>{initials(displayName)}</span>
                    </div>
                  )}
                  {avatarUploading && (
                    <div style={styles.avatarEditUploadingOverlay}>
                      <span style={styles.avatarEditUploadingText}>Uploading...</span>
                    </div>
                  )}
                </div>
                <label
                  className="fl-avatar-camera-btn"
                  style={styles.avatarEditCameraButton}
                  aria-label="Change profile photo"
                >
                  <IconCamera />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    disabled={avatarUploading}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Photo gallery</label>
              <div style={styles.galleryEditGrid}>
                {galleryPhotos.map((photo) => (
                  <div key={photo.id} style={styles.galleryTile}>
                    <img src={photo.photo_url} alt="" style={styles.galleryTileImg} />
                    <button
                      type="button"
                      className="fl-photo-remove-btn"
                      style={styles.galleryRemoveButton}
                      onClick={() => handleDeletePhoto(photo.id)}
                      disabled={deletingPhotoIds.has(photo.id)}
                      aria-label="Delete photo"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <label className="fl-gallery-add-tile">
                  {galleryUploading ? (
                    <span className="fl-gallery-add-label">Uploading...</span>
                  ) : (
                    <>
                      <span className="fl-gallery-add-plus">+</span>
                      <span className="fl-gallery-add-label">Add photo</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleGalleryPhotoAdd}
                    disabled={galleryUploading}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
              {galleryLoading && <p style={{ ...styles.muted, margin: "6px 0 0" }}>Loading photos...</p>}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Skill level</label>
              <select style={styles.input} value={skillLevel} onChange={(e) => setSkillLevel(e.target.value)}>
                {SKILL_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {level[0].toUpperCase() + level.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Preferred runs</label>
              <div style={styles.chipRow}>
                {RUN_OPTIONS.map((run) => (
                  <button
                    type="button"
                    key={run}
                    onClick={() => setPreferredRuns((prev) => toggleInList(prev, run))}
                    style={preferredRuns.includes(run) ? styles.chipActive : styles.chip}
                  >
                    {preferredRuns.includes(run) ? `✓ ${run}` : run}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Available days</label>
              <div style={styles.chipRow}>
                {DAY_OPTIONS.map((day) => (
                  <button
                    type="button"
                    key={day}
                    onClick={() => setDays((prev) => toggleInList(prev, day))}
                    style={days.includes(day) ? styles.chipActive : styles.chip}
                  >
                    {days.includes(day) ? `✓ ${day}` : day}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Time of day</label>
              <div style={styles.chipRow}>
                {TIME_OPTIONS.map((tod) => (
                  <button
                    type="button"
                    key={tod}
                    onClick={() => setTimesOfDay((prev) => toggleInList(prev, tod))}
                    style={timesOfDay.includes(tod) ? styles.chipActive : styles.chip}
                  >
                    {timesOfDay.includes(tod) ? `✓ ${tod}` : tod}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Bio (optional)</label>
              <textarea
                style={{ ...styles.input, minHeight: 70 }}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                maxLength={500}
                placeholder="A line or two about your skiing style..."
              />
            </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button type="submit" style={styles.primaryButton} disabled={saving}>
          {saving ? "Saving..." : "Save profile"}
        </button>
        {profile && (
          <button type="button" style={styles.secondaryButton} onClick={() => setTab(previousTab)}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );

  if (!profile) {
    return (
      <div className="fl-theme" style={styles.page}>
        <div style={styles.card}>
          <h2 style={styles.heading}>Set up your Buddy Finder profile</h2>
          {error && <div style={styles.errorBanner}>{error}</div>}
          {profileForm}
        </div>
      </div>
    );
  }

  return (
    <div className="fl-theme" style={styles.page}>
      <div style={styles.tabRow}>
        <button style={tab === "discover" ? styles.tabActive : styles.tab} onClick={() => setTab("discover")}>
          Discover
        </button>
        <button
          style={tab === "matches" ? styles.tabActive : styles.tab}
          onClick={() => {
            setTab("matches");
            setActiveMatch(null);
          }}
        >
          Matches
        </button>
        <button style={tab === "profile" ? styles.tabActive : styles.tab} onClick={openProfileEditor}>
          Edit profile
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {tab === "profile" && (
        <div style={styles.card}>
          <h2 style={styles.heading}>Edit your profile</h2>
          {profileForm}
        </div>
      )}

      {justMatched && (
        <div style={{ ...styles.card, textAlign: "center", borderColor: "#c9a84c" }}>
          <p style={{ margin: 0 }}>You matched with {justMatched.display_name}!</p>
          <button
            style={styles.primaryButton}
            onClick={() => {
              setTab("matches");
              setJustMatched(null);
            }}
          >
            Say hi
          </button>
        </div>
      )}

      {tab === "discover" && (
        <div>
          {candidatesLoading ? (
            <p style={styles.muted}>Loading skiers nearby...</p>
          ) : candidates.length === 0 ? (
            <div style={{ ...styles.card, textAlign: "center" }}>
              <div style={styles.emptyStateIcon}>
                <MountainIcon />
              </div>
              <p>No more skiers to show right now.</p>
              <button style={styles.secondaryButton} onClick={loadCandidates}>
                Refresh
              </button>
            </div>
          ) : (
            <div style={styles.discoverGrid}>
              {candidates.map((candidate) => (
                <DiscoverCard
                  key={candidate.id}
                  candidate={candidate}
                  isRemoving={removingIds.has(candidate.id)}
                  isSwiping={swipingIds.has(candidate.id)}
                  onOpenProfile={setViewedProfile}
                  onSwipe={handleSwipe}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "matches" && !activeMatch && (
        <div>
          {matchesLoading ? (
            <p style={styles.muted}>Loading matches...</p>
          ) : matches.length === 0 ? (
            <div style={styles.card}>
              <p>No matches yet — keep swiping in Discover!</p>
            </div>
          ) : (
            matches.map((match) => (
              <div
                key={match.match_id}
                style={styles.matchRow}
                onClick={() => {
                  setActiveMatch(match);
                  setMessages([]);
                }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${match.buddy.display_name}'s profile`}
                  className="fl-match-photo-tile"
                  style={styles.matchPhotoTile}
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewedProfile({ ...match.buddy, matchId: match.match_id });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      setViewedProfile({ ...match.buddy, matchId: match.match_id });
                    }
                  }}
                >
                  {match.buddy.avatar_url ? (
                    <img src={match.buddy.avatar_url} alt="" style={styles.cardPhotoImg} />
                  ) : (
                    <div style={styles.cardPhotoPlaceholder}>
                      <span style={styles.matchPhotoInitial}>{initials(match.buddy.display_name)}</span>
                    </div>
                  )}
                  <div style={styles.cardScrim} />
                  {match.has_unread && <span style={styles.matchUnreadDot} />}
                  <div style={styles.matchPhotoOverlay}>
                    <span style={styles.matchPhotoName}>{match.buddy.display_name}</span>
                    <span style={styles.matchPhotoBadge}>{match.buddy.skill_level}</span>
                  </div>
                </div>
                <div style={styles.matchInfo}>
                  <span style={styles.matchCaption}>Matched &middot; {formatMatchedAt(match.matched_at)}</span>
                </div>
                <span style={styles.matchMessagePill}>Message</span>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "matches" && activeMatch && (
        <div style={{ ...styles.card, display: "flex", flexDirection: "column", height: 440 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <div style={styles.chatHeaderPhotoTile}>
                {activeMatch.buddy.avatar_url ? (
                  <img src={activeMatch.buddy.avatar_url} alt="" style={styles.cardPhotoImg} />
                ) : (
                  <div style={styles.cardPhotoPlaceholder}>
                    <span style={styles.chatHeaderPhotoInitial}>{initials(activeMatch.buddy.display_name)}</span>
                  </div>
                )}
              </div>
              <h3 style={{ margin: 0 }}>{activeMatch.buddy.display_name}</h3>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <button type="button" style={styles.reportLink} onClick={() => handleBlock(activeMatch.buddy)}>
                Block
              </button>
              <button style={styles.secondaryButton} onClick={() => setActiveMatch(null)}>
                Back
              </button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
            {messages.length === 0 ? (
              <p style={styles.muted}>Say hi to {activeMatch.buddy.display_name}!</p>
            ) : (
              messages.map((msg) =>
                msg.sender_id === currentUserId ? (
                  <div
                    key={msg.id}
                    style={{
                      alignSelf: "flex-end",
                      background: "#c9a84c",
                      color: "#0d1b2a",
                      borderRadius: 8,
                      padding: "8px 12px",
                      maxWidth: "75%",
                    }}
                  >
                    {msg.body}
                  </div>
                ) : (
                  <div
                    key={msg.id}
                    style={{ alignSelf: "flex-start", maxWidth: "75%", display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <div style={{ background: "#1e3a5f", color: "#ffffff", borderRadius: 8, padding: "8px 12px" }}>
                      {msg.body}
                    </div>
                    <button
                      type="button"
                      style={{ ...styles.reportLink, alignSelf: "flex-start" }}
                      onClick={() => reportContent(apiFetch, "message", msg.id)}
                    >
                      Report
                    </button>
                  </div>
                )
              )
            )}
          </div>
          <form onSubmit={handleSendMessage} style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input
              style={{ ...styles.input, flex: 1 }}
              value={messageDraft}
              onChange={(e) => setMessageDraft(e.target.value)}
              placeholder="Type a message..."
              maxLength={2000}
            />
            <button type="submit" style={styles.primaryButton} disabled={sendingMessage || !messageDraft.trim()}>
              Send
            </button>
          </form>
        </div>
      )}

      {viewedProfile && (
        <div style={styles.modalOverlay} onClick={() => setViewedProfile(null)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalCloseButton} onClick={() => setViewedProfile(null)} aria-label="Close">
              ×
            </button>

            <div style={styles.modalPhotoWrap}>
              {viewedProfile.avatar_url ? (
                <img src={viewedProfile.avatar_url} alt="" style={styles.cardPhotoImg} />
              ) : (
                <div style={styles.cardPhotoPlaceholder}>
                  <span style={styles.cardPhotoInitial}>{initials(viewedProfile.display_name)}</span>
                </div>
              )}
              <div style={styles.cardScrim} />
              <div style={styles.cardNameOverlay}>
                <h3 style={styles.cardName}>{viewedProfile.display_name}</h3>
                <span style={styles.cardSkillBadge}>{viewedProfile.skill_level}</span>
              </div>
            </div>

            <div style={styles.modalBody}>
              <TagRow items={viewedProfile.preferred_runs} rowStyle={styles.cardTagRow} chipStyle={styles.cardTagChip} />
              <TagRow items={viewedProfile.availability?.days} rowStyle={styles.cardTagRow} chipStyle={styles.cardTagChip} />
              <TagRow
                items={viewedProfile.availability?.time_of_day}
                rowStyle={styles.cardTagRow}
                chipStyle={styles.cardTagChip}
              />
              {viewedProfile.bio && <p style={styles.cardBio}>{viewedProfile.bio}</p>}
              {viewedProfilePhotos.length > 0 && (
                <div style={{ ...styles.field, marginTop: 16, marginBottom: 0 }}>
                  <label style={styles.label}>Photos</label>
                  <div style={styles.galleryGrid}>
                    {viewedProfilePhotos.map((url, idx) => (
                      <img
                        key={idx}
                        src={url}
                        alt=""
                        style={{ ...styles.galleryThumb, cursor: "pointer" }}
                        onClick={() => setLightboxPhoto(url)}
                      />
                    ))}
                  </div>
                </div>
              )}
              {viewedProfile.matchId && (
                <button
                  style={styles.modalMessageButton}
                  onClick={() => {
                    setActiveMatch({ match_id: viewedProfile.matchId, buddy: viewedProfile });
                    setMessages([]);
                    setViewedProfile(null);
                  }}
                >
                  Message
                </button>
              )}
              {currentUserId && viewedProfile.id !== currentUserId && (
                <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 16 }}>
                  <button
                    type="button"
                    style={styles.reportLink}
                    onClick={() => reportContent(apiFetch, "profile", viewedProfile.id)}
                  >
                    Report
                  </button>
                  <button type="button" style={styles.reportLink} onClick={() => handleBlock(viewedProfile)}>
                    Block
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {lightboxPhoto && (
        <div style={styles.lightboxOverlay} onClick={() => setLightboxPhoto(null)}>
          <button
            style={styles.lightboxCloseButton}
            onClick={() => setLightboxPhoto(null)}
            aria-label="Close"
          >
            ×
          </button>
          <img src={lightboxPhoto} alt="" style={styles.lightboxImage} />
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#0d1b2a",
    color: "#ffffff",
    fontFamily: "Georgia, 'Times New Roman', serif",
    padding: "24px 16px 64px",
    boxSizing: "border-box",
  },
  card: {
    backgroundColor: "#112236",
    border: "1px solid #1e3a5f",
    borderRadius: 14,
    padding: 20,
    maxWidth: 420,
    margin: "0 auto 16px",
    boxSizing: "border-box",
  },
  heading: { marginTop: 0 },
  emptyStateIcon: {
    width: 64,
    margin: "0 auto 10px",
    opacity: 0.5,
  },
  discoverGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 56,
    minHeight: 560,
    maxWidth: 420,
    margin: "0 auto",
  },
  discoverCard: {
    position: "relative",
    backgroundColor: "#122032",
    borderRadius: 20,
    boxShadow: "0 14px 32px rgba(0,0,0,0.4)",
    overflow: "hidden",
    boxSizing: "border-box",
    cursor: "pointer",
    transition: "opacity 0.25s ease, transform 0.25s ease",
  },
  cardPhotoWrap: {
    position: "relative",
    width: "100%",
    aspectRatio: "4 / 5",
    overflow: "visible",
  },
  cardPhotoImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  cardPhotoPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(160deg,#1c3550 0%,#122032 100%)",
  },
  cardPhotoInitial: {
    fontSize: 72,
    fontWeight: "bold",
    color: "rgba(255,255,255,0.28)",
  },
  cardScrim: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, transparent 42%, rgba(10,20,32,0.94) 100%)",
    pointerEvents: "none",
  },
  cardPhotoCountBadge: {
    position: "absolute",
    top: 14,
    right: 14,
    display: "flex",
    alignItems: "center",
    gap: 5,
    background: "rgba(13,27,42,0.72)",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: "bold",
    padding: "5px 10px",
    borderRadius: 999,
  },
  cardNameOverlay: {
    position: "absolute",
    left: 20,
    right: 20,
    bottom: 22,
  },
  cardName: {
    margin: 0,
    fontSize: 27,
    fontWeight: "bold",
    color: "#ffffff",
    textShadow: "0 2px 10px rgba(0,0,0,0.6)",
  },
  cardSkillBadge: {
    display: "inline-block",
    marginTop: 8,
    background: "#c9a84c",
    color: "#0d1b2a",
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: 11,
    letterSpacing: "0.06em",
    padding: "4px 10px",
    borderRadius: 999,
  },
  cardActionsRow: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    transform: "translateY(50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 22,
    zIndex: 2,
  },
  cardPassButton: {
    width: 52,
    height: 52,
    flexShrink: 0,
    borderRadius: "50%",
    background: "#0d1b2a",
    border: "2px solid #2c4a68",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    boxShadow: "0 6px 16px rgba(0,0,0,0.45)",
  },
  cardLikeButton: {
    width: 60,
    height: 60,
    flexShrink: 0,
    borderRadius: "50%",
    background: "#c9a84c",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    boxShadow: "0 6px 16px rgba(0,0,0,0.45)",
  },
  cardBody: {
    padding: "38px 20px 20px",
    boxSizing: "border-box",
  },
  cardTagRow: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 },
  cardTagChip: {
    display: "inline-block",
    background: "#1c3550",
    color: "#c7d4e0",
    fontSize: 11,
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid #2c4a68",
  },
  cardBio: {
    margin: "10px 0 0",
    fontSize: 14,
    lineHeight: 1.6,
    color: "#c7d4e0",
  },
  tagRow: { display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginBottom: 6 },
  tagChip: {
    display: "inline-block",
    background: "#1e3a5f",
    color: "#ddddee",
    fontSize: 11,
    padding: "3px 8px",
    borderRadius: 999,
  },
  tabRow: { display: "flex", gap: 8, maxWidth: 420, margin: "0 auto 16px" },
  tab: {
    flex: 1,
    background: "transparent",
    border: "1px solid #1e3a5f",
    color: "#ddddee",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  tabActive: {
    flex: 1,
    background: "#c9a84c",
    border: "1px solid #c9a84c",
    color: "#0d1b2a",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  field: { marginBottom: 30 },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: "rgba(201,168,76,0.78)",
    marginBottom: 10,
  },
  avatarEditOuter: {
    position: "relative",
    width: 140,
    height: 140,
    flexShrink: 0,
  },
  avatarEditPhotoBox: {
    position: "relative",
    width: "100%",
    height: "100%",
    borderRadius: 24,
    overflow: "hidden",
    boxSizing: "border-box",
  },
  avatarEditImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  avatarEditInitial: {
    fontSize: 48,
    fontWeight: "bold",
    color: "rgba(255,255,255,0.28)",
  },
  avatarEditUploadingOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(13,27,42,0.66)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarEditUploadingText: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#eef2f6",
  },
  avatarEditCameraButton: {
    position: "absolute",
    bottom: -4,
    right: -4,
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(13,27,42,0.9)",
    border: "3px solid #112236",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(0,0,0,0.45)",
    boxSizing: "border-box",
  },
  input: {
    width: "100%",
    backgroundColor: "#0d1b2a",
    border: "1px solid #3a5a7a",
    color: "#ffffff",
    borderRadius: 8,
    padding: "10px 12px",
    fontFamily: "Georgia, 'Times New Roman', serif",
    // 16px or more: iOS zooms the page into smaller inputs on focus and stays zoomed.
    fontSize: 16,
    boxSizing: "border-box",
  },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  chip: {
    background: "#1c3550",
    border: "1px solid #2c4a68",
    color: "#c7d4e0",
    borderRadius: 999,
    padding: "6px 12px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 13,
  },
  chipActive: {
    background: "#1c3550",
    border: "1.5px solid #c9a84c",
    color: "#c9a84c",
    fontWeight: "bold",
    borderRadius: 999,
    padding: "5.5px 11.5px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 13,
  },
  primaryButton: {
    background: "#c9a84c",
    color: "#0d1b2a",
    border: "none",
    borderRadius: 8,
    padding: "10px 18px",
    fontWeight: "bold",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  secondaryButton: {
    background: "transparent",
    border: "1px solid #3a5a7a",
    color: "#ffffff",
    borderRadius: 8,
    padding: "8px 14px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  matchRow: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 16,
    width: "100%",
    maxWidth: 420,
    margin: "0 auto 10px",
    backgroundColor: "#112236",
    border: "1px solid #1e3a5f",
    borderRadius: 16,
    padding: 12,
    cursor: "pointer",
    textAlign: "left",
    color: "#ffffff",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  matchPhotoTile: {
    position: "relative",
    width: 88,
    height: 88,
    borderRadius: 16,
    overflow: "hidden",
    flexShrink: 0,
    cursor: "pointer",
  },
  matchPhotoInitial: {
    fontSize: 28,
    fontWeight: "bold",
    color: "rgba(255,255,255,0.28)",
  },
  chatHeaderPhotoTile: {
    position: "relative",
    width: 44,
    height: 44,
    borderRadius: 12,
    overflow: "hidden",
    flexShrink: 0,
  },
  chatHeaderPhotoInitial: {
    fontSize: 16,
    fontWeight: "bold",
    color: "rgba(255,255,255,0.28)",
  },
  matchPhotoOverlay: {
    position: "absolute",
    left: 8,
    right: 8,
    bottom: 7,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  matchPhotoName: {
    margin: 0,
    fontSize: 13,
    fontWeight: "bold",
    color: "#ffffff",
    textShadow: "0 2px 6px rgba(0,0,0,0.6)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  matchPhotoBadge: {
    display: "inline-block",
    alignSelf: "flex-start",
    background: "#c9a84c",
    color: "#0d1b2a",
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: 8,
    letterSpacing: "0.05em",
    padding: "2px 6px",
    borderRadius: 999,
  },
  matchUnreadDot: {
    position: "absolute",
    top: -4,
    right: -4,
    width: 14,
    height: 14,
    borderRadius: "50%",
    backgroundColor: "#c9a84c",
    border: "2px solid #112236",
    boxSizing: "border-box",
    zIndex: 1,
  },
  matchInfo: {
    flex: 1,
    minWidth: 0,
  },
  matchCaption: {
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#8fa3b8",
  },
  matchMessagePill: {
    flexShrink: 0,
    background: "transparent",
    border: "1px solid #2c4a68",
    color: "#eef2f6",
    fontWeight: "bold",
    fontSize: 13,
    padding: "8px 16px",
    borderRadius: 999,
  },
  galleryGrid: { display: "flex", flexWrap: "wrap", gap: 10 },
  galleryThumb: {
    width: 64,
    height: 64,
    objectFit: "cover",
    borderRadius: 8,
    border: "1px solid #1e3a5f",
    display: "block",
  },
  galleryEditGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 10,
  },
  galleryTile: {
    position: "relative",
    aspectRatio: "1 / 1",
    borderRadius: 14,
    overflow: "hidden",
    background: "linear-gradient(135deg,#1c3550 0%,#122032 100%)",
    boxSizing: "border-box",
  },
  galleryTileImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  galleryRemoveButton: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: 14,
    lineHeight: 1,
    padding: 0,
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
    zIndex: 2,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    boxSizing: "border-box",
    zIndex: 1000,
  },
  modalCard: {
    position: "relative",
    backgroundColor: "#122032",
    border: "1px solid #2c4a68",
    borderRadius: 20,
    overflow: "hidden",
    maxWidth: 420,
    width: "100%",
    maxHeight: "85vh",
    overflowY: "auto",
    boxSizing: "border-box",
    color: "#ffffff",
    fontFamily: "Georgia, 'Times New Roman', serif",
    boxShadow: "0 14px 32px rgba(0,0,0,0.4)",
  },
  modalPhotoWrap: {
    position: "relative",
    width: "100%",
    aspectRatio: "4 / 5",
    overflow: "hidden",
  },
  modalBody: {
    padding: "20px 20px 24px",
    boxSizing: "border-box",
  },
  reportLink: {
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    color: "#8fa3b8",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 13,
    textDecoration: "underline",
    cursor: "pointer",
  },
  modalMessageButton: {
    display: "block",
    width: "100%",
    marginTop: 20,
    background: "#c9a84c",
    color: "#0d1b2a",
    border: "none",
    borderRadius: 999,
    padding: "14px 20px",
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  modalCloseButton: {
    position: "absolute",
    top: 14,
    right: 14,
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(13,27,42,0.72)",
    border: "none",
    borderRadius: "50%",
    color: "#ffffff",
    fontSize: 20,
    lineHeight: 1,
    cursor: "pointer",
    zIndex: 2,
  },
  lightboxOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.92)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    boxSizing: "border-box",
    zIndex: 1100,
  },
  lightboxImage: {
    maxWidth: "90vw",
    maxHeight: "90vh",
    objectFit: "contain",
    borderRadius: 8,
  },
  lightboxCloseButton: {
    position: "absolute",
    top: 20,
    right: 24,
    background: "transparent",
    border: "none",
    color: "#ffffff",
    fontSize: 32,
    lineHeight: 1,
    cursor: "pointer",
  },
  muted: { color: "#8899aa", fontSize: 13 },
  errorBanner: {
    background: "rgba(255,68,68,0.12)",
    border: "1px solid #ff4444",
    color: "#ff4444",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    marginBottom: 16,
    maxWidth: 420,
    marginLeft: "auto",
    marginRight: "auto",
  },
};
